import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { Button } from './ui';
import { resolveProductImage } from './ProductImage';
import './CartDrawer.css';

const FREE_SHIP_THRESHOLD = 100;

export default function CartDrawer({ open, onClose }) {
  const { cart, getCartTotal, removeFromCart, updateQuantity, priceFor } = useCart();
  const { fmt } = useCurrency();
  const subtotal = getCartTotal();
  const remainingForFreeShip = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);
  const shipPct = Math.min(100, (subtotal / FREE_SHIP_THRESHOLD) * 100);

  const itemKey = (i) => `${i.id}::${i.variant_id || ''}`;

  if (!open) return null;
  return (
    <>
      <div className="cart-drawer__backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="cart-drawer" role="dialog" aria-label="Shopping cart">
        <header className="cart-drawer__header">
          <h2>Your cart ({cart.length})</h2>
          <button type="button" className="cart-drawer__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        {cart.length === 0 ? (
          <div className="cart-drawer__empty">
            <p>Your cart is empty.</p>
            <Button as={Link} to="/shop" onClick={onClose}>Browse cards</Button>
          </div>
        ) : (
          <>
            <div className="cart-drawer__free-ship">
              {remainingForFreeShip > 0 ? (
                <>Add <strong>{fmt(remainingForFreeShip)}</strong> for free shipping</>
              ) : (
                <>You qualify for <strong>free shipping</strong>.</>
              )}
              <div className="cart-drawer__progress" aria-hidden="true">
                <div className="cart-drawer__progress-bar" style={{ width: `${shipPct}%` }} />
              </div>
            </div>

            <ul className="cart-drawer__list">
              {cart.map(item => (
                <li key={itemKey(item)} className="cart-drawer__line">
                  {(() => {
                    const url = resolveProductImage(item);
                    return url
                      ? <img src={url} alt="" className="cart-drawer__thumb" loading="lazy" />
                      : <div className="cart-drawer__thumb cart-drawer__thumb--placeholder" />;
                  })()}
                  <div className="cart-drawer__line-body">
                    <div className="cart-drawer__line-name">{item.name}</div>
                    {item.condition && <div className="cart-drawer__line-meta">{item.condition}</div>}
                    <div className="cart-drawer__line-controls">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>−</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                      <button
                        className="cart-drawer__remove"
                        onClick={() => removeFromCart(item.id)}
                        aria-label={`Remove ${item.name}`}
                      >×</button>
                    </div>
                  </div>
                  <div className="cart-drawer__line-price">
                    {fmt(priceFor(item) * item.quantity)}
                  </div>
                </li>
              ))}
            </ul>

            <footer className="cart-drawer__footer">
              <div className="cart-drawer__subtotal">
                <span>Subtotal</span>
                <strong>{fmt(subtotal)}</strong>
              </div>
              <div className="cart-drawer__actions">
                <Button as={Link} to="/cart" variant="secondary" onClick={onClose} fullWidth>View cart</Button>
                <Button as={Link} to="/checkout" onClick={onClose} fullWidth>Checkout →</Button>
              </div>
            </footer>
          </>
        )}
      </aside>
    </>
  );
}
